import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { RouterModule } from '@angular/router';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';

import { CurrentAthleteService } from '../current-athlete.service';
import { FitDuplicateReview, FitDuplicateReviewService } from './fit-duplicate-review.service';
import {
  FitDuplicateReviewDialogComponent, FitDuplicateReviewDialogResult,
} from './fit-duplicate-review-dialog.component';
import { DeviceDuplicateGroup, DeviceDuplicateService } from './device-duplicate.service';
import { DeviceDuplicateDialogComponent } from './device-duplicate-dialog.component';
import { StrengthMerge, StrengthMergeService } from './strength-merge.service';
import { StrengthConflictDialogComponent } from './strength-conflict-dialog.component';

/**
 * Globales Banner für erkannte Dubletten. Zwei verschiedene Sachverhalte, die
 * bewusst getrennt gemeldet werden, weil die Entscheidung eine andere ist:
 *
 *  1. **Datei-Dublette** — dieselbe Aktivität wurde zweimal importiert (gleicher
 *     Fingerprint bzw. gleiche Startsekunde). Frage: behalten oder verwerfen?
 *  2. **Doppelaufzeichnung** — dieselbe Einheit wurde von ZWEI GERÄTEN
 *     aufgezeichnet (Uhr + Radcomputer) und zählt dadurch doppelt. Frage:
 *     welche der beiden soll in die Auswertungen einfließen?
 *  3. **Kraft-Konflikt** — dieselbe Krafteinheit liegt EINMAL getippt und
 *     EINMAL als Datei vor, beide mit Sätzen. Frage: welches Satzprotokoll
 *     gilt? Der Import entscheidet das ausdrücklich nicht — FIT-Dateien kommen
 *     per Webhook, wenn die App gar nicht offen ist.
 *
 * Alle drei laden bei Athletenwechsel neu.
 */
@Component({
  selector: 'app-fit-duplicate-banner',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatIconModule, MatButtonModule, RouterModule, TranslateModule],
  template: `
    @if (!suppressed) {
      @if (deviceGroups.length) {
        <div class="fd-banner">
          <mat-icon class="fd-icon">content_copy</mat-icon>
          <div class="fd-text">
            <strong>
              {{ (deviceGroups.length === 1
                    ? 'training_analysis.device_duplicates.banner.title_one'
                    : 'training_analysis.device_duplicates.banner.title_many')
                  | translate:{ count: deviceGroups.length } }}
            </strong>
            <span class="fd-body">
              {{ 'training_analysis.device_duplicates.banner.body' | translate }}
            </span>
            <!-- Deep-Link in die Einstellungen: wer das öfter hat, will die
                 Geräte-Präferenz setzen statt jedes Mal zu entscheiden. -->
            <a class="fd-link" routerLink="/profil/einstellungen" fragment="device-duplicates">
              {{ 'training_analysis.device_duplicates.settings.title' | translate }} →
            </a>
          </div>
          <span class="grow"></span>
          <button mat-flat-button color="primary" (click)="reviewDeviceDuplicates()">
            {{ 'training_analysis.device_duplicates.banner.cta' | translate }}
          </button>
          <button mat-icon-button (click)="dismiss()" aria-label="dismiss">
            <mat-icon>close</mat-icon>
          </button>
        </div>
      }

      @if (strengthMerges.length) {
        <div class="fd-banner">
          <mat-icon class="fd-icon">fitness_center</mat-icon>
          <div class="fd-text">
            <strong>
              {{ (strengthMerges.length === 1
                    ? 'training_analysis.strength_merge.banner.title_one'
                    : 'training_analysis.strength_merge.banner.title_many')
                  | translate:{ count: strengthMerges.length } }}
            </strong>
            <span class="fd-body">
              {{ 'training_analysis.strength_merge.banner.body' | translate }}
            </span>
          </div>
          <span class="grow"></span>
          <button mat-flat-button color="primary" (click)="reviewStrengthMerges()">
            {{ 'training_analysis.strength_merge.banner.cta' | translate }}
          </button>
          <button mat-icon-button (click)="dismiss()" aria-label="dismiss">
            <mat-icon>close</mat-icon>
          </button>
        </div>
      }

      @if (reviews.length) {
        <div class="fd-banner">
          <mat-icon class="fd-icon">content_copy</mat-icon>
          <div class="fd-text">
            <strong>{{ reviews.length }} mögliche Dublette(n) importiert</strong>
            <span class="fd-body">Automatisch synchronisierte Aktivität(en) gibt es evtl. schon — bitte prüfen.</span>
          </div>
          <span class="grow"></span>
          <button mat-flat-button color="primary" (click)="review()">Prüfen</button>
          <button mat-icon-button (click)="dismiss()" aria-label="dismiss">
            <mat-icon>close</mat-icon>
          </button>
        </div>
      }
    }
  `,
  styles: [`
    .fd-banner {
      display: flex; align-items: flex-start; gap: 0.6rem;
      padding: 0.6rem 0.85rem; margin: 0.5rem 0;
      background: var(--status-warning-bg, #fff4e5);
      color: var(--status-warning-text, #92400e);
      border-left: 4px solid var(--color-warning, #d97706);
      border-radius: 6px; font-size: var(--fs-sm);
    }
    .fd-icon { color: var(--color-warning, #d97706); flex-shrink: 0; margin-top: 0.1rem; }
    .fd-text { flex: 1; min-width: 0; }
    .fd-text strong { display: block; }
    .fd-body { display: block; margin-top: 0.15rem; }
    .fd-link {
      display: inline-block; margin-top: 0.2rem; font-size: var(--fs-xs, 11.5px);
      color: inherit; text-decoration: underline; opacity: 0.85;
    }
    .grow { flex: 1; }
  `],
})
export class FitDuplicateBannerComponent implements OnInit, OnDestroy {
  private readonly svc = inject(FitDuplicateReviewService);
  private readonly deviceSvc = inject(DeviceDuplicateService);
  private readonly strengthSvc = inject(StrengthMergeService);
  private readonly dialog = inject(MatDialog);
  private readonly currentAthlete = inject(CurrentAthleteService);
  private readonly cdr = inject(ChangeDetectorRef);

  reviews: FitDuplicateReview[] = [];
  deviceGroups: DeviceDuplicateGroup[] = [];
  strengthMerges: StrengthMerge[] = [];
  suppressed = false;
  private sub?: Subscription;
  private muteSub?: Subscription;

  ngOnInit(): void {
    // Bei Athletenwechsel neu laden (Reviews sind pro betrachtetem Athleten). Ohne
    // ausgewählten Athleten NICHT pollen (vermeidet 403-Rauschen vor Login/Auswahl).
    this.sub = this.currentAthlete.athleteId$.subscribe((id) => {
      this.suppressed = false;
      if (id != null) {
        this.refresh();
      } else {
        this.reviews = [];
        this.deviceGroups = [];
        this.strengthMerges = [];
        this.cdr.markForCheck();
      }
    });

    // Wird eine Einheit anderswo ruhig gestellt (Kalender-Menü, Session-Dialog,
    // Einstellungen), kann sich der offene Gruppenbestand ändern.
    this.muteSub = this.deviceSvc.muteChanged$.subscribe(() => this.refresh());
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.muteSub?.unsubscribe();
  }

  private refresh(): void {
    // Ein Fehler auf einer Seite darf die andere nicht mit ausblenden — z.B.
    // fehlt auf einer Umgebung ohne das neue SQL nur der Gruppen-Endpoint.
    forkJoin({
      reviews: this.svc.list().pipe(catchError(() => of([] as FitDuplicateReview[]))),
      groups: this.deviceSvc.list().pipe(catchError(() => of([] as DeviceDuplicateGroup[]))),
      merges: this.strengthSvc.list().pipe(catchError(() => of([] as StrengthMerge[]))),
    }).subscribe(({ reviews, groups, merges }) => {
      this.reviews = reviews ?? [];
      this.deviceGroups = groups ?? [];
      this.strengthMerges = merges ?? [];
      this.cdr.markForCheck();
    });
  }

  review(): void {
    this.dialog
      .open(FitDuplicateReviewDialogComponent, { data: { reviews: this.reviews }, maxWidth: '640px' })
      .afterClosed()
      .subscribe((_res?: FitDuplicateReviewDialogResult) => this.refresh());
  }

  reviewDeviceDuplicates(): void {
    this.dialog
      .open(DeviceDuplicateDialogComponent, {
        data: { groups: this.deviceGroups },
        width: '46rem',
        maxWidth: '95vw',
      })
      .afterClosed()
      .subscribe(() => this.refresh());
  }

  reviewStrengthMerges(): void {
    this.dialog
      .open(StrengthConflictDialogComponent, {
        data: { merges: this.strengthMerges },
        width: '44rem',
        maxWidth: '95vw',
      })
      .afterClosed()
      .subscribe(() => this.refresh());
  }

  dismiss(): void {
    this.suppressed = true;
    this.cdr.markForCheck();
  }
}
